import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Form, Row, Col, Modal } from 'react-bootstrap';
import { PlusIcon, EditIcon, TrashIcon } from '../common/Icons';
import { clientService } from '../../services/api';
import { Client, PaginatedResponse } from '../../types';
import { toast } from 'react-toastify';
import ClientForm from './ClientForm';

const ClientList: React.FC = () => {
  const [clients, setClients] = useState<PaginatedResponse<Client> | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);

  const [showEditModal, setShowEditModal] = useState(false);
  const [clientToEdit, setClientToEdit] = useState<Client | null>(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;

      const response = await clientService.getAll(params);
      setClients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [search]);

  const handleDelete = async () => {
    if (!clientToDelete) return;

    try {
      await clientService.delete(clientToDelete.id);
      toast.success('Client supprimé avec succès');
      fetchClients();
      setShowDeleteModal(false);
      setClientToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div>
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>Gestion des clients</h1>
        <Button variant="primary" onClick={() => setShowAddModal(true)}>
          <PlusIcon className="me-2" />
          Nouveau client
        </Button>
      </div>

      {/* TABLE */}
      <Card>
        <Card.Header>
          <Row>
            <Col md={6}>
              <Form.Control
                type="text"
                placeholder="Rechercher un client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Col>
          </Row>
        </Card.Header>

        <Card.Body>
          {loading ? (
            <div className="text-center">Chargement...</div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Nom</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>ICE</th>
                  <th>Solde</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {clients?.data.map((client) => (
                  <tr key={client.id}>
                    <td>{client.name}</td>
                    <td>{client.email || '-'}</td>
                    <td>{client.phone || '-'}</td>
                    <td>{client.ice || '-'}</td>

                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD',
                      }).format(client.balance)}
                    </td>

                    <td>
                      <span
                        className={`badge ${
                          client.is_active ? 'bg-success' : 'bg-danger'
                        }`}
                      >
                        {client.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>

                    <td>
                      {/* EDIT */}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => {
                          setClientToEdit(client);
                          setShowEditModal(true);
                        }}
                      >
                        <EditIcon />
                      </Button>

                      {/* DELETE */}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => {
                          setClientToDelete(client);
                          setShowDeleteModal(true);
                        }}
                      >
                        <TrashIcon />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirmer la suppression</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          Êtes-vous sûr de vouloir supprimer le client "
          {clientToDelete?.name}" ?
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ADD MODAL */}
      <ClientForm
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        onSuccess={fetchClients}
      />

      {/* EDIT MODAL */}
      <ClientForm
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setClientToEdit(null);
        }}
        onSuccess={fetchClients}
        client={clientToEdit}
      />
    </div>
  );
};

export default ClientList;